import React, { useEffect, useState } from 'react';
import { Avatar, Box, Chip, Divider, Paper, Typography } from '@mui/material';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from 'recharts';
import FlagIcon from '@mui/icons-material/Flag';
import MonitorWeightIcon from '@mui/icons-material/MonitorWeight';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import { PageIndices } from '@/common/i-doc/i-doc.registry.ts';
import { PageHint } from '@components/common/PageHint.tsx';
import { PmClientActivityWidget } from '@components/pm/client/PmClientActivityWidget.tsx';
import { ClientDetails } from '@/types/client';
import { getClientMeasurementHistory } from '@services/metrics.service';
import { getClientResourceUsage } from '@services/client-dashboard.service';
import { ResourceUsageCell } from '@components/admin/ResourceUsageCell.tsx';
import { ResourceUsagePieChart } from '@components/admin/ResourceUsagePieChart.tsx';

const isWeightMeasurement = (name: string) =>
  name.toLowerCase().includes('вага') || name.toLowerCase().includes('weight');

const formatDateTick = (timestamp: number) =>
  new Date(timestamp).toLocaleDateString('uk-UA', { day: '2-digit', month: '2-digit' });

interface WeightPoint { timestamp: number; value: number; }

interface PmClientDashboardTabProps {
  clientId: number;
  client: ClientDetails;
}

// Дашборд клієнта для продакт-менеджера (2026-10-10). Блоки "Клієнт" і "Динаміка ваги" нижче -
// перенесені БЕЗ ЗМІН з експертського ClientDashboardTab.tsx (2026-10-29, легасі-віджети, яких не
// було в PM-панелі) - та сама верстка/логіка, лише своє завантаження даних (тут PM-дашборд не має
// спільного стейту з рештою "нового" дизайну експертської сторінки, звідки їх забрали).
export const PmClientDashboardTab: React.FC<PmClientDashboardTabProps> = ({ clientId, client }) => {
  const g = client.general;
  const [latestWeight, setLatestWeight] = useState<number | null>(null);
  const [weightHistory, setWeightHistory] = useState<WeightPoint[]>([]);
  const [weightUnit, setWeightUnit] = useState('кг');

  useEffect(() => {
    getClientMeasurementHistory(String(clientId), { limit: 90 })
      .then((items) => {
        const weightItems = items
          .filter((i) => isWeightMeasurement(i.measurementType.name))
          .sort((a, b) => new Date(a.measuredAt).getTime() - new Date(b.measuredAt).getTime());

        if (weightItems.length > 0) {
          setLatestWeight(weightItems[weightItems.length - 1].value);
          setWeightUnit(weightItems[0].unit.symbol);
          setWeightHistory(
            weightItems.map((i) => ({
              timestamp: new Date(i.measuredAt).getTime(),
              value: i.value,
            })),
          );
        }
      })
      .catch(() => {});
  }, [clientId]);

  const fullName = [g.name, g.surname].filter(Boolean).join(' ');
  const age = g.bornYear ? new Date().getFullYear() - g.bornYear : null;
  const genderLabel = g.gender === 1 ? 'Чоловіча' : g.gender === 2 ? 'Жіноча' : null;

  return (
    <Box data-i-doc={PageIndices.PM_CLIENT_DASHBOARD}>
      <PageHint iDoc={PageIndices.PM_CLIENT_DASHBOARD} />
      <PmClientActivityWidget clientId={clientId} />

      {/* ── Клієнт (перенесено без змін з ClientDashboardTab.tsx, 2026-10-29) ── */}
      <Paper sx={{ p: 3, mb: 3, mt: 3 }}>
        <Box display="flex" justifyContent="space-between" alignItems="flex-start" gap={2} flexWrap="wrap">
          <Box display="flex" alignItems="center" gap={2}>
            <Avatar src={g.avatar ?? undefined} sx={{ width: 64, height: 64 }}>
              {g.name?.charAt(0).toUpperCase()}
            </Avatar>
            <Box>
              <Typography variant="h5" fontWeight={700}>{fullName || 'Клієнт'}</Typography>
              <Typography variant="body2" color="text.secondary">{g.email}</Typography>
              <Box display="flex" gap={1} mt={0.5} flexWrap="wrap">
                {age && <Chip size="small" label={`${age} р.`} />}
                {genderLabel && <Chip size="small" label={genderLabel} />}
                {g.height && <Chip size="small" label={`${g.height} см`} />}
                {latestWeight && <Chip size="small" icon={<MonitorWeightIcon fontSize="small" />} label={`${latestWeight} кг`} />}
                {g.goal && <Chip size="small" icon={<FlagIcon fontSize="small" />} label={g.goal} color="primary" variant="outlined" />}
                {g.timezone && <Chip size="small" icon={<AccessTimeIcon fontSize="small" />} label={g.timezone} variant="outlined" />}
              </Box>
            </Box>
          </Box>

          {/* Лейзі — довантажується незалежно, не блокує решту дашборду */}
          <Box>
            <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}>
              Використання ресурсів
            </Typography>
            <Box display="flex" alignItems="center" gap={1}>
              <ResourceUsagePieChart id={Number(clientId)} fetchUsage={getClientResourceUsage} />
              <ResourceUsageCell id={Number(clientId)} fetchUsage={getClientResourceUsage} />
            </Box>
          </Box>
        </Box>
        {g.activityLevel && (
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1.5 }}>
            Рівень активності: <b>{g.activityLevel}</b>
          </Typography>
        )}
      </Paper>

      {/* ── Динаміка ваги (перенесено без змін з ClientDashboardTab.tsx, 2026-10-29) ── */}
      {weightHistory.length > 1 && (
        <>
          <Divider sx={{ my: 3 }} />
          <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 1 }} display="flex" alignItems="center" gap={1}>
            <MonitorWeightIcon fontSize="small" color="primary" /> Динаміка ваги
          </Typography>
          <Paper sx={{ p: 2, mb: 3 }}>
            <ResponsiveContainer width="100%" height={240}>
              <LineChart data={weightHistory} margin={{ top: 8, right: 16, left: 0, bottom: 8 }}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis
                  dataKey="timestamp"
                  type="number"
                  domain={['dataMin', 'dataMax']}
                  tickFormatter={formatDateTick}
                  angle={-20}
                  textAnchor="end"
                  height={40}
                />
                <YAxis
                  unit={` ${weightUnit}`}
                  domain={([dataMin, dataMax]: [number, number]) => {
                    const pad = (dataMax - dataMin) * 0.1 || 1;
                    return [Math.floor(dataMin - pad), Math.ceil(dataMax + pad)];
                  }}
                  width={56}
                />
                <Tooltip
                  labelFormatter={(ts) =>
                    new Date(ts).toLocaleDateString('uk-UA', {
                      day: '2-digit',
                      month: 'long',
                      year: 'numeric',
                    })
                  }
                  formatter={(value: number) => [`${value} ${weightUnit}`, 'Вага']}
                />
                <Line
                  type="monotone"
                  dataKey="value"
                  stroke="#1976d2"
                  strokeWidth={2}
                  dot={{ r: 3 }}
                  activeDot={{ r: 6 }}
                />
              </LineChart>
            </ResponsiveContainer>
          </Paper>
        </>
      )}
    </Box>
  );
};
